import { Accordion, AccordionHeader, AccordionItem, AccordionPanel } from "@fluentui/react-components";
import { FC, useEffect, useState } from "react";
import { ICanvasProps } from "./Models";
import { BaseCanvasWrapper, ButtonGroupWrapper, HeaderTitleBarWrapper, TitleGroupWrapper } from "./Styles";
import { RenderIconNarrowRight } from "../Icons/IconsCustom";
import { BuildRCAttribute } from "@/common/function/RCAttribute";
/**
 * The `CanvasProps` interface defines the props for the `Canvas` component.
 *
 * @export
 * @interface CanvasProps
 * @extends {IRCAttributeId}
 */

/**
 * The `Canvas` component is a reusable component that displays a collapsible panel with a header and content.
 * It can be used to display various types of content, such as forms, tables, or charts.
 *
 * @param rcId - The ID of the component, used for tracking and debugging purposes.
 * @param headerTitle - The title of the panel header.
 * @param button - The button to display in the panel header.
 * @param children - The content to display in the panel.
 * @param icon - The icon to display in the panel header.
 * @param isOpen - Whether the panel should be open or closed by default.
 * @param isStickHeader - Whether the panel header should be sticky.
 * @param isStickContent - Whether the panel content should be sticky.
 * @returns A `Canvas` component that displays a collapsible panel with a header and content.
 */
const Canvas: FC<ICanvasProps> = ({ headerTitle, button, children, icon, isOpen, onOpen, theme, rcName }) => {
  const [isAccordionOpen, setIsAccordionOpen] = useState<boolean>(isOpen || false);

  useEffect(() => {
    setIsAccordionOpen(!!isOpen);
  }, [isOpen]);

  const handleAccordionClick = () => {
    setIsAccordionOpen(!isAccordionOpen);
    onOpen && onOpen(!isAccordionOpen);
  };

  return (
    <BaseCanvasWrapper className="BaseCanvasWrapper" $isOpen={isAccordionOpen} $theme={theme} {...BuildRCAttribute(`canvas_base.${rcName || "name"}.${isAccordionOpen}`)}>
      <Accordion collapsible className="canvas_panel" openItems={isAccordionOpen ? "0" : ""}>
        <AccordionItem value="0">
          <AccordionHeader
            onClick={handleAccordionClick}
            expandIconPosition="end"
            expandIcon={<div className="roll_animation"><RenderIconNarrowRight theme={theme} /></div>}
            {...BuildRCAttribute(`canvas_header.${rcName || "name"}`)}
          >
            <HeaderTitleBarWrapper className="HeaderTitleBar">
              {icon}
              <TitleGroupWrapper className="TitleGroup" $theme={theme}>{headerTitle}</TitleGroupWrapper>
              <ButtonGroupWrapper className="ButtonGroup">{button}</ButtonGroupWrapper>
              <div style={{ clear: "both" }}></div>
            </HeaderTitleBarWrapper>
          </AccordionHeader>
          <AccordionPanel className="rc_accordionPanel_content">{children}</AccordionPanel>
        </AccordionItem>
        <AccordionItem value="1"></AccordionItem>
      </Accordion>
    </BaseCanvasWrapper>
  );
};

export default Canvas;
